import { z } from 'zod';

// ---------------------------------------------------------------------------
// Product Activity Log (XT-519) — the append-only audit trail behind the
// product **Activity Log** tab. Shared between the writer (products service),
// the reader (productActivity router) and the web feature so the vocabulary,
// the redaction rules and the summary format cannot drift between them.
// ---------------------------------------------------------------------------

// Controlled action vocabulary.
export const productActivityEventTypeValues = [
  'created',
  'updated',
  'added',
  'removed',
  'published',
  'unpublished',
  'archived',
  'restored',
  'linked',
  'unlinked',
] as const;
export const productActivityEventTypeSchema = z.enum(
  productActivityEventTypeValues
);
export type ProductActivityEventType = z.infer<
  typeof productActivityEventTypeSchema
>;

// What the row is about — the product itself or a related entity.
export const productActivityEntityTypeValues = [
  'product',
  'supplier',
  'pricing',
  'attachment',
  'media',
  'note',
  'facility',
  'label',
  'status',
  'subitem',
  'cms',
] as const;
export const productActivityEntityTypeSchema = z.enum(
  productActivityEntityTypeValues
);
export type ProductActivityEntityType = z.infer<
  typeof productActivityEntityTypeSchema
>;

export const productActivityActorTypeValues = [
  'user',
  'system',
  'migration',
  'integration',
  'support',
] as const;
export const productActivityActorTypeSchema = z.enum(
  productActivityActorTypeValues
);
export type ProductActivityActorType = z.infer<
  typeof productActivityActorTypeSchema
>;

export const productActivitySourceValues = [
  'product_ui',
  'crm_ui',
  'api',
  'import',
  'migration',
  'scheduled_job',
  'admin_tool',
] as const;
export const productActivitySourceSchema = z.enum(productActivitySourceValues);
export type ProductActivitySource = z.infer<typeof productActivitySourceSchema>;

// Non-human actor types. Drives the "System" / "Migration" badge in the table
// so an automated change is never mistaken for a person's edit (§5).
export const isNonHumanActorType = (type: ProductActivityActorType): boolean =>
  type !== 'user' && type !== 'support';

// ---------------------------------------------------------------------------
// Field registry — column name → human label + owning entity. Drives the
// "Field" column, the field filter and the CSV. Unknown columns fall back to
// a de-camelised label so a newly-added product column still reads sensibly
// before anyone updates this map.
// ---------------------------------------------------------------------------

export const PRODUCT_ACTIVITY_FIELDS: Record<
  string,
  { label: string; entityType: ProductActivityEntityType }
> = {
  attachments: { label: 'Attachment', entityType: 'attachment' },
  budgetValue: { label: 'Budget Tier', entityType: 'pricing' },
  catalogId: { label: 'Catalog', entityType: 'product' },
  city: { label: 'City', entityType: 'product' },
  contentCategory: { label: 'Category', entityType: 'product' },
  contentSubcategory: { label: 'Subcategory', entityType: 'product' },
  costVatId: { label: 'Cost VAT', entityType: 'pricing' },
  country: { label: 'Country', entityType: 'product' },
  curatedUrls: { label: 'Curated URLs', entityType: 'cms' },
  currency: { label: 'Currency', entityType: 'pricing' },
  description: { label: 'Description', entityType: 'product' },
  details: { label: 'Details', entityType: 'product' },
  externalProductId: { label: 'External Product ID', entityType: 'product' },
  facilities: { label: 'Facility', entityType: 'facility' },
  groupPriceThresholds: { label: 'Group Pricing', entityType: 'pricing' },
  hideTitle: { label: 'Hide Title', entityType: 'cms' },
  isGross: { label: 'Gross Pricing', entityType: 'pricing' },
  isHidden: { label: 'Status', entityType: 'status' },
  isHiddenOnCms: { label: 'CMS Visibility', entityType: 'cms' },
  isHiddenOnMiniPlanner: {
    label: 'Mini Planner Visibility',
    entityType: 'status',
  },
  legs: { label: 'Location and Time', entityType: 'product' },
  notes: { label: 'Public Item Notes', entityType: 'note' },
  parentId: { label: 'Parent Product', entityType: 'subitem' },
  price: { label: 'Price', entityType: 'pricing' },
  priceVatId: { label: 'Price VAT', entityType: 'pricing' },
  pricingType: { label: 'Pricing Type', entityType: 'pricing' },
  privateNotes: { label: 'Private Item Notes', entityType: 'note' },
  serviceType: { label: 'Service Type', entityType: 'product' },
  stars: { label: 'Stars', entityType: 'product' },
  suppliers: { label: 'Supplier', entityType: 'supplier' },
  tags: { label: 'Label', entityType: 'label' },
  thumbnail: { label: 'Image', entityType: 'media' },
  title: { label: 'Title', entityType: 'product' },
  titleEn: { label: 'Title (EN)', entityType: 'product' },
  transportType: { label: 'Transport Type', entityType: 'product' },
  videos: { label: 'Video', entityType: 'media' },
};

const decamelise = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

export const productActivityFieldLabel = (
  field: string | null | undefined
): string => {
  if (!field) return '—';
  return PRODUCT_ACTIVITY_FIELDS[field]?.label ?? decamelise(field);
};

export const productActivityEntityTypeFor = (
  field: string | null | undefined
): ProductActivityEntityType =>
  (field && PRODUCT_ACTIVITY_FIELDS[field]?.entityType) || 'product';

// Product columns that never belong in an audit trail — they change on every
// save and carry no accountability value.
export const PRODUCT_ACTIVITY_IGNORED_FIELDS: ReadonlySet<string> = new Set([
  'modifiedOn',
  'lastModifiedBy',
  'catalogTitle',
  'costVat',
  'priceVat',
  'defaultBudgetValue',
  'priceCategory',
  // Projected from `details.hideTitleInItinerary` by `byId`. `details` is
  // tracked, so recording the projection too would write two rows for one
  // change — same reason catalogTitle / costVat / priceVat are excluded.
  'hideTitle',
]);

// ---------------------------------------------------------------------------
// Redaction (§4 / §17). Two independent layers:
//   1. Secrets — stripped before serialisation, so they are never persisted.
//   2. Content permissions — fields whose *values* inherit a product-content
//      permission; redacted at read time for callers who lack it.
// ---------------------------------------------------------------------------

export const REDACTED_MARKER = '[redacted]';

/**
 * Keys that must never reach a persisted audit payload. superjson — the tRPC
 * transformer this repo wires into every response — throws on all three, so a
 * row carrying one would insert happily and then fail to serialise on every
 * read, forever, in a table that cannot be repaired or deleted. Assigning
 * `__proto__` would additionally re-parent the record.
 */
export const UNSERIALISABLE_KEYS: ReadonlySet<string> = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

// Matched against the key split into words, so `bankIban` is a secret but
// `libanesTitle` is not, and `refreshToken` is but `tokenizer` is not. An
// unanchored substring match over-redacts real content — which destroys audit
// data just as surely as under-redaction leaks it.
const SECRET_KEY_RE =
  /(^|[^a-z])(pass ?(word|phrase)|secret|token|credential|api[ _-]?key|authorization|auth[ _-]?key|private[ _-]?key|cvv|card[ _-]?number|iban|access[ _-]?key)s?([^a-z]|$)/;

export const isSecretKey = (key: string): boolean =>
  SECRET_KEY_RE.test(key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());

/**
 * Deep-strip secret-looking keys from a JSON-serialisable value. Applied at
 * write time so a credential that leaks into a product payload never reaches
 * the audit table in the first place (§17). Cycles are impossible here — the
 * inputs are already JSON round-tripped DB rows.
 */
export const stripSecrets = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripSecrets);
  // A `Date` (or any boxed value) would otherwise fall into the object branch
  // and be flattened to `{}` — an audit entry silently recording nothing.
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value === 'object') {
    // Must stay a plain, normally-prototyped object: Drizzle's `is()` reads
    // `Object.getPrototypeOf(v).constructor` on every value handed to
    // `.values()`, so a null-prototype object throws and takes the whole
    // product write down with it.
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(
      value as Record<string, unknown>
    )) {
      // Dropped deliberately, not incidentally — see UNSERIALISABLE_KEYS.
      // `product.details` is a `looseObject` on the wire, so any caller with
      // products:update can put these keys in a payload.
      if (UNSERIALISABLE_KEYS.has(key)) continue;
      out[key] = isSecretKey(key) ? REDACTED_MARKER : stripSecrets(entry);
    }
    return out;
  }
  return value;
};

/**
 * Fields whose recorded values inherit a content permission. A caller holding
 * `product_activity:read` but not the listed permission still sees *that* the
 * field changed and by whom — the values are replaced with the redaction
 * marker (§16: "Audit values inherit content permissions").
 */
export const PRODUCT_ACTIVITY_RESTRICTED_FIELDS: Record<
  string,
  { resource: string; action: string }
> = {
  privateNotes: { resource: 'products', action: 'read' },
};

// ---------------------------------------------------------------------------
// Summaries. The list endpoint returns only these; the full values load when
// a row is expanded (§21). Kept in shared so the writer's truncation and the
// reader's "show more" affordance agree on the same boundary.
// ---------------------------------------------------------------------------

export const PRODUCT_ACTIVITY_SUMMARY_MAX = 120;

const HTML_TAG_RE = /<[^>]*>/g;

const scalarSummary = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    return value.replace(HTML_TAG_RE, ' ').replace(/\s+/g, ' ').trim();
  }
  return '';
};

// Pull the most human part out of a structured value: localized text arrays
// (`[{ languageCode, text }]`), relationship rows (`{ name }` / `{ title }`)
// and attachment rows (filename in `file.name` OR `fileNameBase` — both
// shapes exist in the wild, see apps/products/README.md).
const entrySummary = (entry: unknown): string => {
  if (entry === null || entry === undefined) return '';
  if (typeof entry !== 'object') return scalarSummary(entry);
  const row = entry as Record<string, unknown>;
  const file = row.file as { name?: unknown } | undefined;
  const candidate =
    row.text ??
    row.name ??
    (typeof row.title === 'string' ? row.title : undefined) ??
    row.titleEn ??
    row.label ??
    row.fileNameBase ??
    file?.name ??
    row.url ??
    row.id;
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return scalarSummary(candidate);
  }
  return '';
};

// Last-resort rendering for a structured value with no obvious human field —
// a price bag, a details bag. Listing bare key names makes the before and
// after sides render identically ("price, currency" → "price, currency"),
// which hides the very change the row exists to report, so the scalar values
// are carried too.
const keyValueSummary = (value: Record<string, unknown>): string =>
  Object.entries(value)
    .filter(
      ([, entry]) =>
        entry === null || ['string', 'number', 'boolean'].includes(typeof entry)
    )
    .slice(0, 6)
    .map(([key, entry]) => `${key}: ${scalarSummary(entry) || '—'}`)
    .join(', ');

/**
 * One-line readable rendering of an audit value. Structured values collapse to
 * their human parts; anything longer than the cap is cut with an ellipsis and
 * reported as truncated so the UI can offer the full value from the detail
 * view.
 */
export const summariseActivityValue = (
  value: unknown
): { text: string; truncated: boolean } => {
  let raw: string;
  if (Array.isArray(value)) {
    const parts = value.map(entrySummary).filter((p) => p.length > 0);
    raw = parts.length > 0 ? parts.join(', ') : `${value.length} item(s)`;
  } else if (value && typeof value === 'object') {
    const single = entrySummary(value);
    raw =
      single.length > 0
        ? single
        : keyValueSummary(value as Record<string, unknown>) ||
          Object.keys(value as Record<string, unknown>)
            .slice(0, 6)
            .join(', ');
  } else {
    raw = scalarSummary(value);
  }

  if (raw.length === 0) {
    return {
      text: value === null || value === undefined ? '' : '—',
      truncated: false,
    };
  }
  if (raw.length <= PRODUCT_ACTIVITY_SUMMARY_MAX) {
    return { text: raw, truncated: false };
  }
  return {
    text: `${raw.slice(0, PRODUCT_ACTIVITY_SUMMARY_MAX - 1)}…`,
    truncated: true,
  };
};

// ---------------------------------------------------------------------------
// CSV. Escaping lives here (not in the export service) because the same rules
// have to hold for any future export surface.
// ---------------------------------------------------------------------------

// Leading =, +, -, @, tab or CR make a spreadsheet treat the cell as a
// formula. Leading whitespace is skipped first: LibreOffice trims it before
// deciding, so `" =1+1"` is still a formula there. The `'` prefix stops the
// spreadsheet evaluating it — note a plain CSV parser will see that quote as
// part of the value.
const FORMULA_LEAD_RE = /^(?:\s*[=+\-@]|[\t\r])/;

export const escapeCsvCell = (value: string | null | undefined): string => {
  const raw = value ?? '';
  const guarded = FORMULA_LEAD_RE.test(raw) ? `'${raw}` : raw;
  return `"${guarded.replace(/"/g, '""')}"`;
};

export const PRODUCT_ACTIVITY_CSV_HEADER = [
  'Product ID',
  'Product Name',
  'Timestamp (UTC)',
  'Timestamp (Local)',
  'User ID',
  'User Name',
  'Action',
  'Field',
  'Previous Value',
  'New Value',
  'Source',
  'Correlation ID',
] as const;

// ---------------------------------------------------------------------------
// Wire schemas
// ---------------------------------------------------------------------------

export const productActivityActorSchema = z.object({
  id: z.uuid().nullable(),
  displayName: z.string(),
  type: productActivityActorTypeSchema,
});
export type ProductActivityActor = z.infer<typeof productActivityActorSchema>;

/**
 * List-row projection. Deliberately excludes `previousValue`/`newValue` — the
 * jsonb payloads load on expand via `productActivity.detail` (§21).
 */
export const productActivityRowSchema = z.object({
  id: z.uuid(),
  productId: z.uuid(),
  occurredAt: z.string(),
  actor: productActivityActorSchema,
  action: productActivityEventTypeSchema,
  entityType: productActivityEntityTypeSchema,
  entityId: z.string().nullable(),
  field: z.string().nullable(),
  fieldLabel: z.string(),
  previousSummary: z.string().nullable(),
  newSummary: z.string().nullable(),
  // A detail payload exists for this row. Both sides are null for a seeded
  // `created` event, which has no recorded snapshot to expand.
  hasDetail: z.boolean(),
  source: productActivitySourceSchema,
  requestId: z.string().nullable(),
  correlationId: z.string().nullable(),
});
export type ProductActivityRow = z.infer<typeof productActivityRowSchema>;

export const PRODUCT_ACTIVITY_PAGE_SIZE = 50;
export const PRODUCT_ACTIVITY_MAX_PAGE_SIZE = 200;

export const listProductActivityInput = z.object({
  productId: z.uuid(),
  limit: z
    .number()
    .int()
    .positive()
    .max(PRODUCT_ACTIVITY_MAX_PAGE_SIZE)
    .default(PRODUCT_ACTIVITY_PAGE_SIZE),
  // Opaque keyset cursor — `{ occurredAt, id }` base64url-encoded. Keyset
  // rather than offset because the table is append-only and can grow large
  // (§11).
  cursor: z.string().optional(),
  action: productActivityEventTypeSchema.optional(),
  actorUserId: z.uuid().optional(),
  field: z.string().optional(),
  // Validated as instants: both are interpolated into a timestamptz
  // comparison, so an unparseable value would surface as a Postgres cast
  // error (500) rather than a rejected input (400).
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  source: productActivitySourceSchema.optional(),
});
export type ListProductActivityInput = z.infer<typeof listProductActivityInput>;

export const paginatedProductActivitySchema = z.object({
  items: z.array(productActivityRowSchema),
  nextCursor: z.string().nullable(),
  // Total matching the current filters, for the record count under the table.
  // Sent on the FIRST page only (`null` on cursor-fed pages): the count is the
  // same for every page of one filter set, so recomputing it on each "load
  // older" click would buy nothing.
  total: z.number().int().nullable(),
});
export type PaginatedProductActivity = z.infer<
  typeof paginatedProductActivitySchema
>;

export const productActivityDetailInput = z.object({
  productId: z.uuid(),
  id: z.uuid(),
});
export type ProductActivityDetailInput = z.infer<
  typeof productActivityDetailInput
>;

export const productActivityDetailSchema = z.object({
  id: z.uuid(),
  previousValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  // Field values withheld from this caller for lack of a content permission.
  redactedFields: z.array(z.string()),
});
export type ProductActivityDetail = z.infer<typeof productActivityDetailSchema>;

// Export is capped and served inline. Above the cap the caller is told to
// narrow the filters rather than being handed a truncated file (§12) —
// mirrors the products xlsx export contract.
export const PRODUCT_ACTIVITY_EXPORT_ROW_CAP = 5000;

export const exportProductActivityInput = z.object({
  productId: z.uuid(),
  format: z.literal('csv').default('csv'),
  // IANA zone for the "Timestamp (Local)" column. UTC when omitted.
  timezone: z.string().default('UTC'),
  filters: listProductActivityInput
    .omit({ productId: true, limit: true, cursor: true })
    .optional(),
});
export type ExportProductActivityInput = z.infer<
  typeof exportProductActivityInput
>;

export const exportProductActivityResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ok'),
    filename: z.string(),
    base64: z.string(),
    rows: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('over-cap'),
    count: z.number().int().nonnegative(),
    cap: z.number().int().positive(),
  }),
]);
export type ExportProductActivityResult = z.infer<
  typeof exportProductActivityResultSchema
>;

// UI action filter (§10) — "All activity" plus the four first-phase actions.
export const PRODUCT_ACTIVITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All activity' },
  { value: 'updated', label: 'Updated' },
  { value: 'created', label: 'Created' },
  { value: 'added', label: 'Added' },
  { value: 'removed', label: 'Removed' },
] as const;

export const PRODUCT_ACTIVITY_ACTION_LABELS: Record<
  ProductActivityEventType,
  string
> = {
  created: 'Created',
  updated: 'Updated',
  added: 'Added',
  removed: 'Removed',
  published: 'Published',
  unpublished: 'Unpublished',
  archived: 'Archived',
  restored: 'Restored',
  linked: 'Linked',
  unlinked: 'Unlinked',
};
